import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ConnectStepsRequest,
  type ConnectStepsResponse,
  type DisconnectStepsRequest,
  type DisconnectStepsResponse,
  type FlowPositionItem,
  type GetProcessLayoutResponse,
  type UpdateFlowPositionsRequest,
  type UpdateFlowPositionsResponse
} from '@bliks-io/contracts';
import {
  isEventNodeId,
  parseEventNodeId,
  resolveStepIdFromNodeId,
  type EdgeLayoutUpdate
} from '@bliks-io/ui';
import { useConnectDraftSteps, useDisconnectDraftSteps } from '@/api/mutations/useProcessDraft';
import { useUpdateProcessLayout } from '@/api/mutations/useUpdateProcessLayout';
import { useDraftGesture } from '@/api/mutations/helpers/draftMutation';
import { processLayoutKeys } from '@/api/query-keys';
import { DraftNodeNotFoundError } from '@/api/draftGraph';
import {
  attachEventToStep,
  detachEventFromStep,
  upgradeLegacyEventInput
} from '../edge-actions/shared';
import { ensureNotTerminal } from '../edge-actions/successorInvariant';
import type { EdgeActionDeps } from '../edge-actions/types';
import { findEdgeRuleId } from '../graph/edgeRules';
import { buildLayoutPatchFromPositions } from './buildLayoutPatchFromPositions';
import { useProcessDraftCache } from './useProcessDraftCache';

interface UseConnectionActionsArgs {
  processId: string;
  edgeActionDeps: EdgeActionDeps;
}

export interface ConnectionActions {
  onConnectSteps: (dto: ConnectStepsRequest) => Promise<ConnectStepsResponse>;
  onDisconnectSteps: (dto: DisconnectStepsRequest) => Promise<DisconnectStepsResponse>;
  onSavePositions: (dto: UpdateFlowPositionsRequest) => Promise<UpdateFlowPositionsResponse>;
  onUpdateEdgeLayouts: (updates: EdgeLayoutUpdate[]) => Promise<void>;
}

export function useConnectionActions({
  processId,
  edgeActionDeps
}: UseConnectionActionsArgs): ConnectionActions {
  const queryClient = useQueryClient();
  const connectMutation = useConnectDraftSteps(processId);
  const disconnectMutation = useDisconnectDraftSteps(processId);
  const updateLayoutMutation = useUpdateProcessLayout(processId, 'draft');
  const { getCachedSteps } = useProcessDraftCache(processId);
  const { graph } = edgeActionDeps;

  // Unified event nodes render under their raw node id — resolve event-ness
  // through the draft graph. Returns the canonical event id (= the node id
  // for event rows), or null when the id isn't an event node.
  const resolveRawEventId = useCallback(
    (nodeId: string): string | null => (graph.findNode(nodeId)?.kind === 'event' ? nodeId : null),
    [graph]
  );

  const connectAsync = connectMutation.mutateAsync;
  const disconnectAsync = disconnectMutation.mutateAsync;
  const updateLayoutAsync = updateLayoutMutation.mutateAsync;

  const doDisconnect = useCallback(
    async (sourceStableId: string, targetStableId: string, ruleId?: string) => {
      await disconnectAsync({
        stepId: graph.resolveNodeRef(sourceStableId, 'step').backendId,
        targetStepId: graph.resolveNodeRef(targetStableId, ['step', 'assignment']).backendId,
        ...(ruleId ? { ruleId } : {})
      });
    },
    [disconnectAsync, graph]
  );

  const doSavePositions = useCallback(
    async (positions: FlowPositionItem[]) => {
      if (positions.length === 0) return;
      const cached = queryClient.getQueryData<GetProcessLayoutResponse>(
        processLayoutKeys.detail(processId, 'draft')
      );
      const patch = buildLayoutPatchFromPositions(positions, cached);
      if (Object.keys(patch).length === 0) return;
      await updateLayoutAsync(patch);
    },
    [processId, queryClient, updateLayoutAsync]
  );

  const onConnectSteps = useDraftGesture(
    processId,
    async ({
      sourceStepId,
      targetStepId,
      sourceRuleId
    }: ConnectStepsRequest): Promise<ConnectStepsResponse> => {
      // Event nodes aren't step documents — the connect endpoint can't route
      // them. The unified attach covers every event kind: dropping the START
      // event on a step additionally marks it as a first step (the backend
      // sets `previousStep = <startEventNodeId>` alongside the eventId input
      // link). Unified nodes carry their raw event id; legacy snapshot nodes
      // (`event-<stepId>-<idx>`) get upgraded server-side first so the drag
      // resolves to a canonical eventId before attach runs.
      const isRawEventSource = resolveRawEventId(sourceStepId) !== null;
      if (isRawEventSource || isEventNodeId(sourceStepId)) {
        let eventId: string | null = resolveRawEventId(sourceStepId);
        if (!eventId) {
          eventId = parseEventNodeId(sourceStepId);
        }
        if (!eventId) {
          eventId = await upgradeLegacyEventInput(sourceStepId, edgeActionDeps);
        }
        if (!eventId) return { updated: false };

        // Dropping an event onto another event node is meaningless — events
        // have no inputs.
        if (resolveRawEventId(targetStepId) !== null) return { updated: false };
        const resolvedTarget = resolveStepIdFromNodeId(targetStepId);
        if (!resolvedTarget) return { updated: false };
        const fired = await attachEventToStep(eventId, resolvedTarget, edgeActionDeps);
        return { updated: fired };
      }

      const sourceStable = resolveStepIdFromNodeId(sourceStepId);
      const targetStable = resolveStepIdFromNodeId(targetStepId);
      if (!sourceStable || !targetStable) return { updated: false };

      // The connect endpoint routes by step `_id` and only steps can hold
      // successors — an assignment source is a graph-shape violation, not a
      // request to send anyway (it used to 404 server-side). Resolve both ids
      // before any cache write, so a stale id is a no-op with nothing to undo.
      let stepId: string;
      let targetBackendId: string;
      try {
        stepId = graph.resolveNodeRef(sourceStable, 'step').backendId;
        targetBackendId = graph.resolveNodeRef(targetStable, ['step', 'assignment']).backendId;
      } catch (err) {
        if (err instanceof DraftNodeNotFoundError) return { updated: false };
        throw err;
      }

      // Optimistic-only: the connect endpoint clears the source's terminal
      // marker in the same request (`clearEndedSuccessorStatus`).
      ensureNotTerminal(sourceStable, sourceRuleId, edgeActionDeps);

      await connectAsync({
        stepId,
        body: { targetStepId: targetBackendId, ...(sourceRuleId ? { ruleId: sourceRuleId } : {}) }
      });
      return { updated: true };
    }
  );

  const onDisconnectSteps = useDraftGesture(
    processId,
    async ({
      sourceStepId: rawSource,
      targetStepId: rawTarget,
      ruleId: requestedRuleId
    }: DisconnectStepsRequest): Promise<DisconnectStepsResponse> => {
      // Event edge disconnect strips the matching event input from the
      // target step; the event entity stays on the draft for re-attach. For
      // the START event the backend also clears `previousStep` and legacy
      // tags. Legacy event node ids are upgraded first so the detach
      // resolves to a canonical eventId.
      if (resolveRawEventId(rawSource) !== null || isEventNodeId(rawSource)) {
        let eventId: string | null = resolveRawEventId(rawSource);
        if (!eventId) {
          eventId = parseEventNodeId(rawSource);
        }
        if (!eventId) {
          eventId = await upgradeLegacyEventInput(rawSource, edgeActionDeps);
        }
        if (!eventId) return { updated: false };
        const resolvedTarget = resolveStepIdFromNodeId(rawTarget);
        if (!resolvedTarget) return { updated: false };
        const fired = await detachEventFromStep(eventId, resolvedTarget, edgeActionDeps);
        return { updated: fired };
      }

      const sourceStable = resolveStepIdFromNodeId(rawSource);
      const targetStable = resolveStepIdFromNodeId(rawTarget);
      // Nothing to disconnect when either end is synthetic, or when both
      // resolve to the same step (the internal X→gateway-X split).
      if (!sourceStable || !targetStable || sourceStable === targetStable) {
        return { updated: false };
      }
      // Caller-supplied output scope (the rule-successor sidebar passes the
      // clicked rule) wins; otherwise derive the edge's rule from the cache.
      const ruleId =
        requestedRuleId ?? findEdgeRuleId(sourceStable, targetStable, getCachedSteps());
      try {
        await doDisconnect(sourceStable, targetStable, ruleId);
      } catch (err) {
        if (err instanceof DraftNodeNotFoundError) return { updated: false };
        throw err;
      }
      return { updated: true };
    }
  );

  const onSavePositions = useCallback(
    async ({ positions }: UpdateFlowPositionsRequest): Promise<UpdateFlowPositionsResponse> => {
      await doSavePositions(positions);
      return { updated: positions.length };
    },
    [doSavePositions]
  );

  const onUpdateEdgeLayouts = useCallback(
    async (updates: EdgeLayoutUpdate[]): Promise<void> => {
      if (updates.length === 0) return;
      const patch = buildLayoutPatchFromPositions(
        [],
        queryClient.getQueryData<GetProcessLayoutResponse>(
          processLayoutKeys.detail(processId, 'draft')
        ),
        updates.map((u) => ({
          sourceNodeId: u.sourceNodeId,
          targetNodeId: u.targetNodeId,
          sourceHandleId: u.sourceHandleId,
          waypoints: u.waypoints
        }))
      );
      await updateLayoutAsync(patch);
    },
    [processId, queryClient, updateLayoutAsync]
  );

  return useMemo(
    () => ({ onConnectSteps, onDisconnectSteps, onSavePositions, onUpdateEdgeLayouts }),
    [onConnectSteps, onDisconnectSteps, onSavePositions, onUpdateEdgeLayouts]
  );
}
